import React from "react";
import { Button, HStack, IconButton } from "@chakra-ui/react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";

const PaginationComponent = ({
    currentPage,
    totalPages,
    onPageChange,
    onPrev,
    onNext,
    size = "sm",
    availablePages = null, // Array of page numbers that are available for navigation
    hasNextToken = false, // For token-based pagination to indicate if next page is available
    ...props
}) => {
    const pages = Array.from({ length: totalPages }, (_, index) => index + 1);
    
    // Function to check if a page is available for navigation
    const isPageAvailable = (page) => {
        if (availablePages) {
            return availablePages.includes(page);
        }
        return true; // Default behavior - all pages available
    };

    const renderPages = () => {
        if (totalPages <= 10) {
            return pages.map((page) => {
                const isAvailable = isPageAvailable(page);
                return (
                    <Button
                        key={page}
                        onClick={() => isAvailable ? onPageChange(page) : null}
                        variant={currentPage === page ? "solid" : "ghost"}
                        colorPalette="blue"
                        size={size}
                        borderRadius="full"
                        isDisabled={!isAvailable}
                        opacity={!isAvailable ? 0.5 : 1}
                        cursor={!isAvailable ? "not-allowed" : "pointer"}
                    >
                        {page}
                    </Button>
                );
            });
        }

        const visiblePages = [];
        visiblePages.push(1);
        if (currentPage > 3) {
            visiblePages.push("...");
        }
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        for (let i = start; i <= end; i++) {
            if (i !== 1 && i !== totalPages) {
                visiblePages.push(i);
            }
        }
        if (currentPage < totalPages - 2) {
            visiblePages.push("...");
        }
        visiblePages.push(totalPages);

        return visiblePages.map((page, index) =>
            page === "..." ? (
                <Button key={`ellipsis-${index}`} variant="ghost" colorPalette="blue" size={size} borderRadius="full" isDisabled>
                    ...
                </Button>
            ) : (
                <Button
                    key={page}
                    onClick={() => isPageAvailable(page) ? onPageChange(page) : null}
                    variant={currentPage === page ? "solid" : "ghost"}
                    colorPalette="blue"
                    size={size}
                    borderRadius="full"
                    isDisabled={!isPageAvailable(page)}
                    opacity={!isPageAvailable(page) ? 0.5 : 1}
                    cursor={!isPageAvailable(page) ? "not-allowed" : "pointer"}
                >
                    {page}
                </Button>
            )
        )
    };

    // For token-based pagination, use hasNextToken to determine if next is available
    const isNextDisabled = hasNextToken !== undefined ? !hasNextToken : currentPage === totalPages;

    return (
        <HStack spacing={4} {...props}>
            <IconButton 
                colorPalette="blue" 
                variant="ghost" 
                borderRadius="full" 
                onClick={onPrev}
                isDisabled={currentPage === 1}
            >
                <LuChevronLeft />
            </IconButton>
            {renderPages()}
            <IconButton 
                colorPalette="blue" 
                variant="ghost" 
                borderRadius="full" 
                onClick={onNext}
                isDisabled={isNextDisabled}
            >
                <LuChevronRight />
            </IconButton>
        </HStack>
    );
};

export default PaginationComponent;
